import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppChoice from "@toothfairy/shared-ui/AppChoice";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMultipleSelectInput from "@toothfairy/shared-ui/AppMultipleSelectInput";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popable } from "react-native-popable";
import { Platform, View } from "react-native";
import DelayedRender from "../../Components/DelayRenderer";
import DocumentSearchModal from "../../Components/DocumentSearchModal";
import EditorWrapper from "../../Components/EditorWrapper";
import GenericViewer from "../../Components/GenericViewer";
import ArtifactViewer from "../../Components/ArtifactViewer";
import AppJoyRide from "../../Components/Joyride";
import LoadingPlaceholder from "../../Components/LoadingPlaceholder";
import PDFViewer from "../../Components/PDFViewer";
import PageTitle from "../../Components/PageTitle";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyClickableOption from "../../Components/ToothFairyComponents/ToothFairyClickableOption";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairyResponsiveOptionsModal from "../../Components/ToothFairyComponents/ToothFairyResponsiveOptionsModal";
import ToothFairySupportButton from "../../Components/ToothFairyComponents/ToothFairySupportButton";
import ToothFairySwitch from "../../Components/ToothFairyComponents/ToothFairySwitch";
import SavingNotification from "../../Components/TrainSwitcher/SavingNotification";
import AppTreeView from "../../Components/TreeView";
import Constants from "../../Constants";
import useUserHelperPreferences from "../../Hooks/useUserHelperPreferences";
import useOnboardingTour, { TOUR_FLAGS } from "../../Hooks/useOnboardingTour";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory } from "../../Router";
import { isConnectedToCloud } from "../../../App";
import {
  isLargeScreen,
  isMobileScreen,
  minAppWidth,
} from "../MainViewContainer";
import TopicsSelectionComponents from "../MrcDocuments/Components/TopicsSelectionComponent";
import SettingsAI from "../Settings/Components/SettingsAI";
import NerViewComponent from "./Components/NerViewComponent";
import TopicsSelectionComponent from "./Components/TopicsSelectionComponent";
import ImagesExtraction from "./Components/ImagesExtraction";

const ARTIFACT_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "mp3",
  "m4a",
  "aac",
  "flac",
  "ogg",
  "mp4",
  "webm",
  "mov",
];
const isArtifactType = (ext) =>
  ARTIFACT_EXTENSIONS.includes(ext?.toLowerCase());

// Advanced vectorisation + images extraction toggles shown in the upload
// modals, under "Publish on upload". Uses the new AppSwitch experience (label
// + description) as in QaSettingsAIView. Both are optional per-document
// features billed per minute of compute (min. 1 min) from the UoI balance.
const UploadProcessingSwitches = ({
  advEmb,
  onAdvEmbChange,
  extractImages,
  onExtractImagesChange,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  return (
    <View
      style={{
        marginTop: 4,
        marginBottom: 30,
        zIndex: -1,
      }}
    >
      <AppSwitch
        wrapperStyle={[
          {
            maxHeight: 30,
            // paddingHorizontal: 6,
            // paddingVertical: 16,
            marginTop: 0,
          },
        ]}
        showTextInputLabel={true}
        label={t("mrcdocs.form.advVectorisation")}
        displayIsTicked={true}
        isTicked={advEmb}
        onChange={(v) => onAdvEmbChange(v)}
        description={t(
          "mrcdocs.ui.advVectorisationDesc",
          "Dedicated enterprise-grade embedding that significantly improves retrieval accuracy for your AI agents. Billed per minute of compute (min. 1 min) from your UoI balance.",
        )}
        descriptionIcon={false}
        descriptionColor={currentTheme?.theme?.mediumGrey}
        name="uploadAdvEmb"
      />
      <AppSwitch
        wrapperStyle={[
          {
            maxHeight: 30,
            // paddingHorizontal: 6,
            // paddingVertical: 16,
            marginTop: 40,
            marginBottom: 30,
          },
        ]}
        showTextInputLabel={true}
        label={t("mrcdocs.form.imgExtraction")}
        displayIsTicked={true}
        isTicked={extractImages}
        onChange={(v) => onExtractImagesChange(v)}
        description={t(
          "mrcdocs.ui.imgExtractionDesc",
          "Extracts images from documents and PDFs so your agents can retrieve them when answering questions. Billed per minute of compute (min. 1 min) from your UoI balance.",
        )}
        descriptionIcon={false}
        descriptionColor={currentTheme?.theme?.mediumGrey}
        name="uploadExtractImages"
      />
    </View>
  );
};

const steps_first_interaction = (t) => [
  {
    target: ".upload_pdf",
    content: t("mrcdocs.joyride.uploadMenu"),
    placement: "left",
    disableBeacon: true,
  },
  {
    target: ".document_section",
    content: t("mrcdocs.joyride.documentSection"),
    placement: "right",
    disableBeacon: true,
  },
  {
    target: ".documents_tree",
    content: t("mrcdocs.joyride.documentsTree"),
    placement: "right",
    disableBeacon: true,
  },
];

const MrcDocumentsView = ({
  isInactive,
  isReadyForRTC,
  isDocumentValid,
  onSocketConnectionChange,
  handleEditorError,
  handleClickOnNode,
  setSelectedNodeForTreeView,
  value,
  setValue,
  documentId,
  createDocument,
  SaveFolder,
  selectedNode,
  deleteNode,
  showModal,
  setShowModal,
  setDialogActionOnTree,
  treeViewActionContext,
  isTreeDataLoading,
  isLoading,
  storeInError,
  storeInProgress,
  isSaved,
  selectedTopics,
  onSelectedTopicsChange,
  topics,
  handleEntityCreation,
  onDrop,
  initialOpen,
  _coreTreeViewData,
  selectedNodeToDelete,
  editDocumentTitle,
  creationofNewDocumentInProgress,
  wordsCount,
  charactersCount,
  agcEnabled,
  fakeCheckInProgress,
  fakeCheckData,
  isCollapsed,
  setIsCollapsed,
  onMove,
  handleOnRecorderClick,
  setEditor,
  documentStatus,
  onDocumentStatusChange = () => {},
  handleFileSelect = () => {},
  handleFolderSelect = () => {},
  handleArtifactSelect = () => {},
  handleArtifactImports = () => {},
  handleDocumentSelection = () => {},
  onTopicCreated = () => {},
  isTopicUpdateModeEnabled,
  setIsTopicUpdateModeEnabled,
  filePath,
  folderPath,
  artifactPath,
  onPDFDocumentLoad = () => {},
  isPDF,
  isPDFLoaded,
  isPDFUploadInProgress,
  folders,
  embeddingStatus,
  embeddingProgress,
  fileUploadProgress,
  setTreeViewActionContext,
  selectedNodeForTreeview,
  downloadDocument = () => {},
  updateDocumentFile = async () => {},
  onUpdateFileClick = () => {},
  updateFileInputRef = null,
  isUpdateFileUploadInProgress = false,
  onDroppedFiles = () => {},
  onDropFilesOnNode = () => {},
  handleDroppedUpload = () => {},
  dropFolderFiles = [],
  dropFolderName = "",
  dropTargetNode = null,
  handleImports = () => {},
  handleFolderImports = () => {},
  batchUploadStatus,
  setPublishOnUpload,
  publishOnUpload,
  uploadAdvEmb,
  setUploadAdvEmb,
  uploadExtractImages,
  setUploadExtractImages,
  setSelectedTopicsForPdfUpload,
  selectedTopicsForPdfUpload,
  onSelectedTopicsChangeForPdfUpload = () => {},
  counter,
  extractImages,
  setExtractImages,
  advEmb,
  setAdvEmb,
  isExtractImagesAllowed,
  searchText,
  setSearchText,
  promiseOptions,
  setLastCancellation,
  embeddingsByDocumentResult,
  embeddingsByDocumentInProgress,
  fetchEmbeddingsByDocument = () => {},
  setIsSaveRequested,
  savingInProgress,
  documentImgRetrievalInstructions,
  handleImagesExtraction = () => {},
  uploadFolderName = "",
  uploadFileCount = 0,
  categorizedFiles = { pdfFiles: [], genericFiles: [] },
  uploadFolderOptions = [],
  uploadTargetFolderId = "",
  onUploadTargetFolderChange = () => {},
  isMultiDeleteMode = false,
  setIsMultiDeleteMode = () => {},
  toggleMultiDeleteMode = () => {},
  multiSelectedIds = [],
  toggleNodeSelection = () => {},
  deleteMultipleNodes = () => {},
  bulkMoveNodes = () => {},
  showBulkMoveModal = false,
  setShowBulkMoveModal = () => {},
  bulkMoveTargetFolderId = null,
  setBulkMoveTargetFolderId = () => {},
  bulkMoveTopics = null,
  setBulkMoveTopics = () => {},
  bulkMovePublish = true,
  setBulkMovePublish = () => {},
  isBulkMoveInProgress = false,
}) => {
  const { t } = useTranslation();
  const maxNerByChunk = 10;
  const AppToasty = AppToast.useToast();
  const formValidationSchema = AppForms.yup.object().shape({
    name:
      treeViewActionContext === "UPLOAD_FILES" ||
      treeViewActionContext === "UPLOAD_FOLDER"
        ? AppForms.yup.string().nullable().label(t("mrcdocs.form.name"))
        : AppForms.yup.string().required().label(t("mrcdocs.form.name")),
    path: AppForms.yup.string().nullable().label(t("mrcdocs.form.path")),
  });
  let filesArray = [];
  if (
    filePath &&
    (treeViewActionContext === "UPLOAD_FILES" ||
      treeViewActionContext === "UPLOAD_PDF")
  )
    filesArray = Array.from(filePath);
  else if (artifactPath && treeViewActionContext === "UPLOAD_ARTIFACTS")
    filesArray = Array.from(artifactPath);
  else if (
    folderPath &&
    (treeViewActionContext === "UPLOAD_FOLDER" ||
      treeViewActionContext === "UPLOAD_PDF_FOLDER")
  )
    filesArray = Array.from(folderPath);
  const { currentTheme } = AppTheme.useTheme();
  const formRef = useRef();
  const [isModalOptionsOpen, setIsModalOptionsOpen] = useState(false);
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const dragHideTimerRef = useRef(null);
  const isFileDrag = (e) =>
    !!e?.dataTransfer &&
    Array.from(e.dataTransfer.types || []).includes("Files");
  const cancelDragHide = () => {
    if (dragHideTimerRef.current) {
      clearTimeout(dragHideTimerRef.current);
      dragHideTimerRef.current = null;
    }
  };
  const handleDragEnter = (e) => {
    if (Platform.OS !== "web" || !isFileDrag(e)) return;
    e.preventDefault();
    cancelDragHide();
    setIsFileDragActive(true);
  };
  const handleDragLeave = (e) => {
    if (Platform.OS !== "web" || !isFileDrag(e)) return;
    e.preventDefault();
    cancelDragHide();
    dragHideTimerRef.current = setTimeout(
      () => setIsFileDragActive(false),
      120,
    );
  };
  const handleDragOver = (e) => {
    if (Platform.OS !== "web" || !isFileDrag(e)) return;
    e.preventDefault();
    cancelDragHide();
    if (!isFileDragActive) setIsFileDragActive(true);
  };
  const handleDrop = (e) => {
    if (Platform.OS !== "web" || !isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    cancelDragHide();
    setIsFileDragActive(false);
    if (isPDFUploadInProgress || !isConnectedToCloud) return;
    let folderId = null;
    try {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      folderId =
        el
          ?.closest?.("[data-drop-folder-id]")
          ?.getAttribute("data-drop-folder-id") || null;
    } catch (error) {}
    onDroppedFiles(e?.dataTransfer, folderId);
  };
  useEffect(() => {
    const resetDrag = () => {
      cancelDragHide();
      setIsFileDragActive(false);
    };
    window.addEventListener("drop", resetDrag, true);
    window.addEventListener("dragend", resetDrag);
    return () => {
      cancelDragHide();
      window.removeEventListener("drop", resetDrag, true);
      window.removeEventListener("dragend", resetDrag);
    };
  }, []);
  const { user } = AppUser.useUser();
  const { updateUserHelperPreferences } = useUserHelperPreferences();
  const {
    needsTour,
    completeTourIfEligible,
    isPlatformSupported,
    useTourReady,
  } = useOnboardingTour();
  const knowledgeHubTourReady = useTourReady([".upload_pdf"]);
  const { getActiveWorkspace, getWorkspaceEntities } = useWorkspaces();
  const history = useHistory();
  const [isInstructionsWindowOpened, setIsIstructionsWindowOpened] = useState(
    false,
  );

  useEffect(() => {
    if (!showModal && formRef?.current) formRef?.current?.resetForm();
  }, [showModal, formRef?.current]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUploadDropdown && !event.target.closest(".upload_pdf")) {
        setShowUploadDropdown(false);
      }
    };
    if (Platform.OS === "web" && showUploadDropdown) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      if (Platform.OS === "web") {
        document.removeEventListener("click", handleClickOutside);
      }
    };
  }, [showUploadDropdown]);

  const _editorStyles = useMemo(() => {
    return {
      wrapperStyle: {
        // backgroundColor: currentTheme?.theme?.softBackgroundColor,
        marginBottom: 10,
        flex: 1,
        padding: 10,
        paddingTop: 15,
        marginLeft: 20,
        borderWidth: 1,

        borderRadius: 10,
        borderColor: currentTheme?.theme?.border_color,
      },
      style: {
        overflowY: Platform.OS === "web" ? "auto" : "scroll",
        flex: 1,
        zIndex: -1,
        fontFamily: "normalFont",
      },
      titleStyle: {
        fontSize: currentTheme?.theme?.base_font_size,
      },
    };
  }, [currentTheme]);
  const _selectedNodeType = selectedNode?.data?.droppable ? "node" : "document";
  const [lastModalOpen, setLastModalOpen] = useState("");

  const isHTML =
    selectedNode?.data?.external_path?.split(".")?.pop() === "html";
  const isRawText =
    selectedNode?.data?.external_path?.split(".")?.pop() === "txt" ||
    selectedNode?.data?.external_path?.split(".")?.pop() === "md";
  const _coreModalView = useMemo(() => {
    const handleFormSubmit = (values) => {
      if (treeViewActionContext === "NEW_FOLDER")
        SaveFolder({}, values.name, false);
      else if (treeViewActionContext === "EDIT_FOLDER")
        SaveFolder(selectedNode, values.name, true);
      else if (treeViewActionContext === "NEW_DOCUMENT")
        createDocument(values.name, values?.topics, values?.status);
      else if (
        treeViewActionContext === "EDIT_DOCUMENT" ||
        treeViewActionContext === "EDIT_PDF" ||
        treeViewActionContext === "EDIT_GENERIC"
      )
        editDocumentTitle(values.name);
    };

    const generateModalTitle = () => {
      if (treeViewActionContext === "NEW_FOLDER")
        return t("mrcdocs.modal.newFolder");
      else if (treeViewActionContext === "EDIT_FOLDER")
        return t("mrcdocs.modal.editFolder");
      else if (treeViewActionContext === "NEW_DOCUMENT")
        return t("mrcdocs.modal.newLiveDocument");
      else if (treeViewActionContext === "UPLOAD_FILES")
        return t("mrcdocs.modal.uploadFiles");
      else if (treeViewActionContext === "UPLOAD_ARTIFACTS")
        return t("mrcdocs.modal.uploadArtifacts");
      else if (treeViewActionContext === "UPLOAD_FOLDER")
        return t("mrcdocs.modal.uploadFolder");
      else if (treeViewActionContext === "UPLOAD_DROP")
        return t("mrcdocs.modal.uploadFiles");
      else if (treeViewActionContext === "EDIT_DOCUMENT")
        return t("mrcdocs.modal.editDocumentTitle");
      else if (treeViewActionContext === "EDIT_PDF")
        return t("mrcdocs.modal.editPdfDocumentTitle");
    };
    const entityName = () => {
      if (
        treeViewActionContext === "NEW_FOLDER" ||
        treeViewActionContext === "EDIT_FOLDER" ||
        treeViewActionContext === "UPLOAD_FOLDER"
      )
        return t("mrcdocs.entity.folder");
      else if (
        treeViewActionContext === "NEW_DOCUMENT" ||
        treeViewActionContext === "EDIT_DOCUMENT"
      )
        return t("mrcdocs.entity.document");
      else if (
        treeViewActionContext === "UPLOAD_FILES" ||
        treeViewActionContext === "UPLOAD_FOLDER" ||
        treeViewActionContext === "UPLOAD_ARTIFACTS"
      ) {
        const pdfCount = categorizedFiles?.pdfFiles?.length || 0;
        const genericCount = categorizedFiles?.genericFiles?.length || 0;
        if (pdfCount > 0 && genericCount > 0)
          return t("mrcdocs.entity.document");
        else if (pdfCount > 0) return t("mrcdocs.entity.pdfDocument");
        else return t("mrcdocs.entity.document");
      }
    };

    const isUploadAction =
      treeViewActionContext === "UPLOAD_FILES" ||
      treeViewActionContext === "UPLOAD_ARTIFACTS" ||
      treeViewActionContext === "UPLOAD_DROP" ||
      treeViewActionContext === "UPLOAD_FOLDER";

    const _uploadFolderSelector =
      isUploadAction && uploadFolderOptions?.length > 0 ? (
        <View
          style={{
            marginBottom: 16,
            marginTop: 4,
            marginLeft: -4,
          }}
        >
          <AppDropDown
            showTextInputLabel
            label={t("mrcdocs.ui.uploadTargetFolder")}
            dropDownStyle={{
              minWidth: Platform.OS === "web" ? 300 : 250,
              borderColor: currentTheme?.theme?.border_color,
            }}
            isElevated={false}
            items={uploadFolderOptions}
            propertyLabel="title"
            propertyValue="id"
            selectedValue={uploadTargetFolderId}
            onValueChange={onUploadTargetFolderChange}
          />
        </View>
      ) : null;

    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                maxWidth: 600,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={showModal}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible={!isPDFUploadInProgress}
        modalTitle={generateModalTitle()}
        modalInnerContentStyle={{
          paddingHorizontal: 50,
          paddingVertical: 20,
        }}
        closeModal={() => {
          setLastModalOpen(Date.now());
          setShowModal(false);
          setTreeViewActionContext("");
          setSelectedTopicsForPdfUpload(null);
          setPublishOnUpload(true);
          setUploadAdvEmb(false);
          setUploadExtractImages(false);
          formRef?.current?.resetForm();
        }}
      >
        {treeViewActionContext === "DELETE_NODE" ? (
          <AppChoice
            wrapperStyle={{
              alignSelf: "center",
              maxWidth: 600,
              marginTop: 50,
            }}
            cancelCaption={t(
              `documents.${_selectedNodeType}DeleteCancelCaption`,
            )}
            confirmCaption={t(
              `documents.${_selectedNodeType}DeleteConfirmCaption`,
            )}
            onConfirmButtonPress={() => {
              deleteNode(selectedNodeToDelete);
            }}
            onCancelButtonPress={() => {
              setShowModal(false);
            }}
            AppChoiceButton={ToothFairyButton}
            caption={t(`documents.${_selectedNodeType}DeleteCaption`)}
            subCaption={t(`documents.${_selectedNodeType}DeleteSubCaption`)}
          />
        ) : treeViewActionContext === "DELETE_MULTIPLE" ? (
          (() => {
            const selectedNodes = (_coreTreeViewData || []).filter((n) =>
              multiSelectedIds.includes(n.id),
            );
            const folderCount = selectedNodes.filter((n) => n.droppable).length;
            const fileCount = selectedNodes.filter((n) => !n.droppable).length;
            return (
              <AppChoice
                wrapperStyle={{
                  alignSelf: "center",
                  maxWidth: 600,
                  marginTop: 50,
                }}
                cancelCaption={t("mrcdocs.ui.multiDeleteCancelCaption")}
                confirmCaption={t("mrcdocs.ui.multiDeleteConfirmCaption")}
                onConfirmButtonPress={() => {
                  deleteMultipleNodes(multiSelectedIds);
                }}
                onCancelButtonPress={() => {
                  setShowModal(false);
                }}
                AppChoiceButton={ToothFairyButton}
                caption={t("mrcdocs.ui.multiDeleteCaption", {
                  fileCount,
                  folderCount,
                })}
                subCaption={t("mrcdocs.ui.multiDeleteSubCaption")}
              />
            );
          })()
        ) : treeViewActionContext === "UPLOAD_DROP" ? (
          <>
            <View style={{ padding: 20, overflowY: "auto" }}>
              <AppText
                preventTranslation
                wrapperStyle={{ paddingBottom: 10 }}
                color={currentTheme?.theme?.primary}
              >
                {t("mrcdocs.ui.dropUploadTitle")}
              </AppText>

              {(categorizedFiles?.pdfFiles?.length > 0 ||
                categorizedFiles?.genericFiles?.length > 0) && (
                <View style={{ paddingBottom: 14 }}>
                  <AppText
                    preventTranslation
                    fontWeight="bold"
                    wrapperStyle={{ paddingBottom: 6 }}
                    color={currentTheme?.theme?.textColor}
                  >
                    {t("mrcdocs.ui.dropDocuments")} (
                    {(categorizedFiles?.pdfFiles?.length || 0) +
                      (categorizedFiles?.genericFiles?.length || 0)}
                    )
                  </AppText>
                  {[
                    ...(categorizedFiles?.pdfFiles || []),
                    ...(categorizedFiles?.genericFiles || []),
                  ]?.map(
                    (file) =>
                      file?.name && (
                        <View
                          key={`doc-${file.name}`}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingBottom: 6,
                            maxWidth: 450,
                          }}
                        >
                          <AppText
                            preventTranslation
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: 400,
                            }}
                            fontWeight="bold"
                          >
                            {file?.name}
                          </AppText>
                          {batchUploadStatus[file?.name] && (
                            <AppText
                              preventTranslation
                              fontWeight="bold"
                              color={
                                batchUploadStatus[file?.name] == 100
                                  ? currentTheme?.theme?.green
                                  : currentTheme?.theme?.orange
                              }
                            >
                              {` ${Math.floor(batchUploadStatus[file?.name])}%`}
                            </AppText>
                          )}
                        </View>
                      ),
                  )}
                </View>
              )}

              {categorizedFiles?.artifactFiles?.length > 0 && (
                <View style={{ paddingBottom: 14 }}>
                  <AppText
                    preventTranslation
                    fontWeight="bold"
                    wrapperStyle={{ paddingBottom: 6 }}
                    color={currentTheme?.theme?.textColor}
                  >
                    {t("mrcdocs.ui.dropArtifacts")} (
                    {categorizedFiles?.artifactFiles?.length})
                  </AppText>
                  {categorizedFiles?.artifactFiles?.map(
                    (file) =>
                      file?.name && (
                        <View
                          key={`art-${file.name}`}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingBottom: 6,
                            maxWidth: 450,
                          }}
                        >
                          <AppText
                            preventTranslation
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: 400,
                            }}
                            fontWeight="bold"
                          >
                            {file?.name}
                          </AppText>
                          {batchUploadStatus[file?.name] && (
                            <AppText
                              preventTranslation
                              fontWeight="bold"
                              color={
                                batchUploadStatus[file?.name] == 100
                                  ? currentTheme?.theme?.green
                                  : currentTheme?.theme?.orange
                              }
                            >
                              {` ${Math.floor(batchUploadStatus[file?.name])}%`}
                            </AppText>
                          )}
                        </View>
                      ),
                  )}
                </View>
              )}

              {dropFolderFiles?.length > 0 && (
                <View style={{ paddingBottom: 14 }}>
                  <AppText
                    preventTranslation
                    fontWeight="bold"
                    wrapperStyle={{ paddingBottom: 6 }}
                    color={currentTheme?.theme?.textColor}
                  >
                    {t("mrcdocs.ui.dropFolder")} "{dropFolderName}" (
                    {dropFolderFiles.length} {t("mrcdocs.ui.dropFiles")})
                  </AppText>
                </View>
              )}

              {_uploadFolderSelector}
              <TopicsSelectionComponents
                showAddNew
                onTopicCreated={onTopicCreated}
                showEditIcon={false}
                isTopicUpdateModeEnabled={true}
                selectedTopics={selectedTopicsForPdfUpload}
                onSelectedTopicsChange={onSelectedTopicsChangeForPdfUpload}
                topics={topics}
                wrapperContainerStyle={{ flex: 1, marginLeft: -5 }}
              />
              <ToothFairySwitch
                wrapperStyle={{
                  flex: 1,
                  // backgroundColor: currentTheme?.theme?.inputBackgroundColor,
                  marginTop: 20,
                  zIndex: -1,
                  marginVertical: 30,
                }}
                label={t("mrcdocs.ui.publishOnUpload")}
                onChange={(v) => setPublishOnUpload(v)}
                isTicked={publishOnUpload}
                description={t(
                  "mrcdocs.ui.publishOnUploadDesc",
                  "Uploaded documents are immediately available to your AI agents. When disabled, documents are kept as drafts you can publish later.",
                )}
                descriptionIcon={false}
                descriptionColor={currentTheme?.theme?.mediumGrey}
              />
              <UploadProcessingSwitches
                advEmb={uploadAdvEmb}
                onAdvEmbChange={setUploadAdvEmb}
                extractImages={uploadExtractImages}
                onExtractImagesChange={setUploadExtractImages}
              />
              <ToothFairyButton
                isDisabled={isPDFUploadInProgress}
                title={
                  isPDFUploadInProgress
                    ? t("mrcdocs.ui.inProgress")
                    : t("mrcdocs.ui.uploadAll")
                }
                onPress={() => handleDroppedUpload()}
                wrapperStyle={{ alignSelf: "center" }}
              />
            </View>
          </>
        ) : treeViewActionContext !== "UPLOAD_FILES" &&
          treeViewActionContext !== "UPLOAD_ARTIFACTS" ? (
          <AppForms.AppForm
            ref={formRef}
            enableReinitialize
            key={lastModalOpen}
            style={{
              minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 300,
            }}
            initialValues={{
              name: treeViewActionContext?.includes("EDIT")
                ? selectedNodeForTreeview?.data?.name
                : treeViewActionContext === "UPLOAD_FOLDER"
                ? uploadFolderName
                : null,
              path: null,
            }}
            onSubmit={async (e) => {
              if (
                treeViewActionContext !== "UPLOAD_FILES" &&
                treeViewActionContext !== "UPLOAD_FOLDER" &&
                !treeViewActionContext?.includes("EDIT") &&
                treeViewActionContext !== "NEW_FOLDER" &&
                treeViewActionContext !== "EDIT_FOLDER" &&
                treeViewActionContext !== "EDIT_DOCUMENT" &&
                treeViewActionContext !== "EDIT_GENERIC"
              ) {
                handleFormSubmit({
                  ...e,
                  topics: selectedTopicsForPdfUpload,
                  status: publishOnUpload ? "published" : "draft",
                });
              } else if (
                treeViewActionContext === "EDIT_FOLDER" ||
                treeViewActionContext === "NEW_FOLDER"
              ) {
                SaveFolder(
                  selectedNodeForTreeview,
                  e.name,
                  treeViewActionContext === "EDIT_FOLDER",
                );
              } else if (treeViewActionContext?.includes("EDIT")) {
                editDocumentTitle(e?.name);
              } else if (treeViewActionContext === "UPLOAD_FOLDER") {
                if (
                  !selectedTopicsForPdfUpload ||
                  selectedTopicsForPdfUpload?.length === 0
                )
                  return AppToasty.show(t("toasts.topics.selectAtLeastOne"), {
                    type: "danger",
                  });
                await handleFolderImports(e.name);
                return;
              } else if (treeViewActionContext === "UPLOAD_FILES") {
                if (
                  !selectedTopicsForPdfUpload ||
                  selectedTopicsForPdfUpload?.length === 0
                )
                  return AppToasty.show(t("toasts.topics.selectAtLeastOne"), {
                    type: "danger",
                  });
                handleImports();
              }
              setLastModalOpen(Date.now());
              setShowModal(false);
              setTreeViewActionContext("");
              setSelectedTopicsForPdfUpload(null);
              setPublishOnUpload(true);
              setUploadAdvEmb(false);
              setUploadExtractImages(false);
              formRef?.current?.resetForm();
            }}
            validationSchema={formValidationSchema}
          >
            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              name="name"
              label={`${entityName()} ${t("mrcdocs.form.name")}`}
              placeholder={`${entityName()} ${t("mrcdocs.form.name")}`}
              formFieldContainerStyle={{
                marginLeft: -5,
              }}
            />
            {treeViewActionContext === "NEW_DOCUMENT" && (
              <>
                <TopicsSelectionComponents
                  showAddNew
                  onTopicCreated={onTopicCreated}
                  showEditIcon={false}
                  isTopicUpdateModeEnabled={true}
                  selectedTopics={selectedTopicsForPdfUpload}
                  onSelectedTopicsChange={onSelectedTopicsChangeForPdfUpload}
                  topics={topics}
                  wrapperContainerStyle={{
                    flex: 1,
                    marginLeft: 0,
                  }}
                />
                <ToothFairySwitch
                  wrapperStyle={{
                    flex: 1,

                    marginTop: 20,
                    zIndex: -1,
                    marginVertical: 30,
                    marginHorizontal: 5,
                  }}
                  label={t("mrcdocs.form.publishOnCreation")}
                  onChange={(v) => {
                    setPublishOnUpload(v);
                  }}
                  isTicked={publishOnUpload}
                />
              </>
            )}
            {treeViewActionContext === "UPLOAD_FOLDER" && (
              <>
                {_uploadFolderSelector}
                <AppText
                  preventTranslation
                  wrapperStyle={{
                    paddingBottom: 5,
                    marginTop: 10,
                    marginLeft: 10,
                  }}
                  color={currentTheme?.theme?.darkGrey}
                  style={{
                    fontSize: 12,
                  }}
                >
                  {uploadFileCount} {t("mrcdocs.ui.filesWillBeUploaded")}
                </AppText>
                <TopicsSelectionComponents
                  showAddNew
                  onTopicCreated={onTopicCreated}
                  showEditIcon={false}
                  isTopicUpdateModeEnabled={true}
                  selectedTopics={selectedTopicsForPdfUpload}
                  onSelectedTopicsChange={onSelectedTopicsChangeForPdfUpload}
                  topics={topics}
                  wrapperContainerStyle={{
                    flex: 1,
                    marginLeft: -5,
                  }}
                />
                <ToothFairySwitch
                  wrapperStyle={{
                    flex: 1,
                    // backgroundColor: currentTheme?.theme?.inputBackgroundColor,
                    marginTop: 20,
                    zIndex: -1,
                    marginVertical: 30,
                    marginHorizontal: 5,
                  }}
                  label={t("mrcdocs.form.publishOnUpload")}
                  onChange={(v) => {
                    setPublishOnUpload(v);
                  }}
                  isTicked={publishOnUpload}
                  description={t(
                    "mrcdocs.ui.publishOnUploadDesc",
                    "Uploaded documents are immediately available to your AI agents. When disabled, documents are kept as drafts you can publish later.",
                  )}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <UploadProcessingSwitches
                  advEmb={uploadAdvEmb}
                  onAdvEmbChange={setUploadAdvEmb}
                  extractImages={uploadExtractImages}
                  onExtractImagesChange={setUploadExtractImages}
                />
              </>
            )}

            <AppForms.AppSubmitButton
              title={t("mrcdocs.form.save")}
              forceEnabled={treeViewActionContext === "UPLOAD_FOLDER"}
              isDisabled={
                creationofNewDocumentInProgress || isPDFUploadInProgress
              }
              awesomeIconColor={currentTheme?.theme?.white}
              backgroundColor={currentTheme?.theme?.primary}
              btnContentStyle={{
                paddingLeft: 0,
                paddingRight: 0,
              }}
              style={[
                {
                  height: Constants.appButtonHeight,
                  width: "auto",
                  shadowColor: currentTheme?.theme?.transparent,
                  marginHorizontal: 10,
                },
              ]}
              wrapperStyle={[
                {
                  width: "fit-content",
                  minWidth: 150,
                  marginTop: 20,
                  alignSelf: "center",
                  height: Constants.appButtonHeight,
                },
              ]}
            />
          </AppForms.AppForm>
        ) : (
          <>
            <View
              style={{
                padding: 20,
                overflowY: "auto",
              }}
            >
              <AppText
                preventTranslation
                wrapperStyle={{
                  paddingBottom: 10,
                }}
                color={currentTheme?.theme?.primary}
              >
                {t("mrcdocs.ui.selectedFiles")}
              </AppText>
              {filesArray?.map(
                (file) =>
                  file?.name && (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingBottom: 10,
                        maxWidth: 450,
                      }}
                    >
                      <AppText
                        preventTranslation
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: 400,
                        }}
                        fontWeight="bold"
                      >
                        {file?.name || t("mrcdocs.ui.noFileSelected")}
                      </AppText>
                      {batchUploadStatus[file?.name] && (
                        <AppText
                          preventTranslation
                          style={{
                            marginLeft: 10,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                          fontWeight="bold"
                          color={
                            batchUploadStatus[file?.name] == 100
                              ? currentTheme?.theme?.green
                              : currentTheme?.theme?.orange
                          }
                        >
                          {` ${Math.floor(batchUploadStatus[file?.name])}%`}
                        </AppText>
                      )}
                    </View>
                  ),
              )}
              {_uploadFolderSelector}
              <TopicsSelectionComponents
                showAddNew
                onTopicCreated={onTopicCreated}
                showEditIcon={false}
                isTopicUpdateModeEnabled={true}
                selectedTopics={selectedTopicsForPdfUpload}
                onSelectedTopicsChange={onSelectedTopicsChangeForPdfUpload}
                topics={topics}
                wrapperContainerStyle={{
                  flex: 1,
                  marginLeft: -5,
                }}
              />
              <ToothFairySwitch
                wrapperStyle={{
                  flex: 1,
                  // backgroundColor: currentTheme?.theme?.inputBackgroundColor,
                  marginTop: 20,
                  zIndex: -1,
                  marginVertical: 30,
                }}
                label={t("mrcdocs.ui.publishOnUpload")}
                onChange={(v) => {
                  setPublishOnUpload(v);
                }}
                isTicked={publishOnUpload}
                description={t(
                  "mrcdocs.ui.publishOnUploadDesc",
                  "Uploaded documents are immediately available to your AI agents. When disabled, documents are kept as drafts you can publish later.",
                )}
                descriptionIcon={false}
                descriptionColor={currentTheme?.theme?.mediumGrey}
              />
              {treeViewActionContext === "UPLOAD_FILES" && (
                <UploadProcessingSwitches
                  advEmb={uploadAdvEmb}
                  onAdvEmbChange={setUploadAdvEmb}
                  extractImages={uploadExtractImages}
                  onExtractImagesChange={setUploadExtractImages}
                />
              )}
              <ToothFairyButton
                isDisabled={isPDFUploadInProgress}
                title={
                  isPDFUploadInProgress
                    ? t("mrcdocs.ui.inProgress")
                    : t("mrcdocs.ui.upload")
                }
                onPress={() => {
                  if (
                    !selectedTopicsForPdfUpload ||
                    selectedTopicsForPdfUpload?.length === 0
                  )
                    return AppToasty.show(t("toasts.topics.selectAtLeastOne"), {
                      type: "danger",
                    });
                  if (treeViewActionContext === "UPLOAD_ARTIFACTS") {
                    handleArtifactImports();
                  } else {
                    handleImports();
                  }
                }}
                wrapperStyle={{
                  alignSelf: "center",
                }}
              />
            </View>
          </>
        )}
      </ToothFairyModal>
    );
  }, [
    setShowModal,
    SaveFolder,
    lastModalOpen,
    currentTheme,
    showModal,
    isLargeScreen(),
    selectedNode?.id,
    treeViewActionContext,
    creationofNewDocumentInProgress,
    filePath,
    folderPath,
    artifactPath,
    isPDFUploadInProgress,
    fileUploadProgress,
    publishOnUpload,
    uploadAdvEmb,
    uploadExtractImages,
    JSON.stringify(selectedTopicsForPdfUpload),
    JSON.stringify(batchUploadStatus),
    topics?.length,
    handleImports,
    handleFolderImports,
    uploadFolderName,
    uploadFileCount,
    categorizedFiles,
    JSON.stringify(multiSelectedIds),
    deleteMultipleNodes,
    _coreTreeViewData,
    handleDroppedUpload,
    JSON.stringify(dropFolderFiles),
    dropFolderName,
    uploadFolderOptions,
    uploadTargetFolderId,
    onUploadTargetFolderChange,
  ]);
  const _bulkMoveModal = useMemo(() => {
    const selectedNodes = (_coreTreeViewData || []).filter((n) =>
      multiSelectedIds.includes(n.id),
    );
    const fileCount = selectedNodes.filter((n) => !n.droppable).length;
    const folderCount = selectedNodes.filter((n) => n.droppable).length;
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                maxWidth: 600,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={showBulkMoveModal}
        modalTitle={t("mrcdocs.ui.bulkMoveTitle", {
          defaultValue: "Move / edit selected",
        })}
        isCloseButtonVisible={!isBulkMoveInProgress}
        closeModal={() => {
          setShowBulkMoveModal(false);
        }}
      >
        <View style={{ padding: 20, overflowY: "auto" }}>
          <AppText
            wrapperStyle={{ paddingBottom: 10 }}
            color={currentTheme?.theme?.mediumGrey}
          >
            {t("mrcdocs.ui.bulkMoveSubtitle", {
              defaultValue: `${fileCount} file(s), ${folderCount} folder(s) selected`,
              fileCount,
              folderCount,
            })}
          </AppText>
          <View style={{ marginBottom: 16, marginTop: 4, marginLeft: -4 }}>
            <AppDropDown
              showTextInputLabel
              label={t("mrcdocs.ui.targetFolder", {
                defaultValue: "Target folder",
              })}
              dropDownStyle={{
                minWidth: Platform.OS === "web" ? 300 : 250,
                borderColor: currentTheme?.theme?.border_color,
              }}
              isElevated={false}
              items={[
                {
                  id: "__no_folder_change__",
                  title: t("mrcdocs.ui.noFolderChange", {
                    defaultValue: "No folder change",
                  }),
                },
                ...(uploadFolderOptions || []),
              ]}
              propertyLabel="title"
              propertyValue="id"
              selectedValue={bulkMoveTargetFolderId || "__no_folder_change__"}
              onValueChange={setBulkMoveTargetFolderId}
            />
          </View>
          <TopicsSelectionComponents
            showAddNew
            onTopicCreated={onTopicCreated}
            showEditIcon={false}
            isTopicUpdateModeEnabled={true}
            selectedTopics={bulkMoveTopics}
            onSelectedTopicsChange={setBulkMoveTopics}
            topics={topics}
            wrapperContainerStyle={{ flex: 1, marginLeft: -5 }}
          />
          <ToothFairySwitch
            wrapperStyle={{
              flex: 1,

              marginTop: 20,
              zIndex: -1,
              marginVertical: 30,
            }}
            label={t("mrcdocs.status.published", { defaultValue: "Published" })}
            onChange={(v) => setBulkMovePublish(v)}
            isTicked={bulkMovePublish}
          />
          <ToothFairyButton
            isDisabled={isBulkMoveInProgress}
            title={
              isBulkMoveInProgress
                ? t("mrcdocs.ui.inProgress")
                : t("mrcdocs.ui.applyChanges", {
                    defaultValue: "Apply changes",
                  })
            }
            onPress={() =>
              bulkMoveNodes({
                selectedIds: multiSelectedIds,
                targetFolderId: bulkMoveTargetFolderId,
                topics: bulkMoveTopics,
                publish: bulkMovePublish,
              })
            }
            wrapperStyle={{ alignSelf: "center" }}
          />
        </View>
      </ToothFairyModal>
    );
  }, [
    showBulkMoveModal,
    isBulkMoveInProgress,
    bulkMoveTargetFolderId,
    JSON.stringify(bulkMoveTopics),
    bulkMovePublish,
    JSON.stringify(multiSelectedIds),
    _coreTreeViewData,
    uploadFolderOptions,
    topics?.length,
    currentTheme,
    bulkMoveNodes,
  ]);
  const _saveStatus = useMemo(() => {
    if (!documentId)
      return {
        label: t("mrcdocs.saveStatus.noDocumentSelected"),
        color: "primary",
        dotValue: 0,
        isProgress: false,
        icon: AppAssets.icons.save,
      };
    else if (!documentId)
      return {
        label: t("mrcdocs.saveStatus.parseMode"),
        color: "mediumGrey",
        dotValue: 0,
        isProgress: true,
      };
    else {
      if (!storeInError && !storeInProgress && isSaved)
        return {
          label: t("mrcdocs.saveStatus.saved"),
          color: "green",
          dotValue: 1,
          isProgress: false,
          icon: AppAssets.icons.save,
        };
      else if (storeInProgress)
        return {
          label: t("mrcdocs.saveStatus.saving"),
          color: "orange",
          dotValue: 1,
          isProgress: true,
          icon: AppAssets.icons.save,
        };
      else if (!isSaved)
        return {
          label: t("mrcdocs.saveStatus.notSaved"),
          color: "red",
          dotValue: 1,
          isProgress: true,
          icon: AppAssets.icons.save,
        };
      else if (storeInError)
        return {
          label: t("mrcdocs.saveStatus.errorWhileSaving"),
          color: "red",
          dotValue: 1,
          isProgress: true,
          icon: AppAssets.icons.save,
        };
      else
        return {
          label: "",
          dotValue: 0,
        };
    }
  }, [documentId, isSaved, storeInError, storeInProgress]);
  const _coreSourcesModelView = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                maxHeight: "90%",
                minHeight: 300,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={showSourcesModal}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("mrcdocs.modal.documentChunks")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
        closeModal={() => {
          setShowSourcesModal(false);
        }}
      >
        {embeddingsByDocumentInProgress ? (
          <ToothFairyLoadingPlaceholder
            style={{
              maxHeight: 200,
            }}
            caption="messages.loading"
            brandedAnimation
          />
        ) : (
          <View>
            {embeddingsByDocumentResult?.data?.length > 0 ? (
              embeddingsByDocumentResult?.data?.map((e) => {
                return (
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderBottomColor: currentTheme?.theme?.border_color,
                      paddingBottom: 5,
                    }}
                  >
                    <AppText
                      style={{
                        fontWeight: "bold",
                        paddingVertical: 5,
                      }}
                      color={currentTheme?.theme?.primary}
                      preventTranslation
                    >
                      Chunk id: <strong>{e?.chunk_id}</strong>
                    </AppText>
                    <AppText
                      style={{
                        fontSize: currentTheme?.theme?.extra_small_font_size,
                        paddingVertical: 10,
                        width: "-webkit-fill-available",
                      }}
                      wrapperStyle={{
                        marginTop: 10,
                      }}
                    >
                      {e?.raw_text}
                    </AppText>
                    <NerViewComponent
                      ners={e?.ners}
                      chunk_id={e?.chunk_id}
                      showHover={false}
                    />
                  </View>
                );
              })
            ) : (
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                }}
              >
                {t("mrcdocs.ui.noDataSourcesAvailable")}
              </AppText>
            )}
          </View>
        )}
      </ToothFairyModal>
    );
  }, [
    JSON.stringify(embeddingsByDocumentResult),
    showSourcesModal,
    embeddingsByDocumentInProgress,
  ]);
  const _savingNotification = useMemo(() => {
    return (
      <SavingNotification
        showEditBtn={documentId}
        color={_saveStatus?.color}
        dotValue={_saveStatus?.dotValue}
        icon={_saveStatus?.icon}
        isProgress={_saveStatus?.isProgress}
        label={_saveStatus?.label}
        isEditEnabled={isTopicUpdateModeEnabled}
        setIsEditEnabled={setIsTopicUpdateModeEnabled}
        setLastCancellation={setLastCancellation}
        setIsSaveRequested={setIsSaveRequested}
        embeddingProgress={embeddingProgress}
        embeddingStatus={embeddingStatus}
        setShowSourcesModal={setShowSourcesModal}
        fetchEmbeddingsByDocument={fetchEmbeddingsByDocument}
      >
        {topicSelectionComponent}
      </SavingNotification>
    );
  }, [
    _saveStatus,
    isTopicUpdateModeEnabled,
    setIsTopicUpdateModeEnabled,
    documentId,
    setLastCancellation,
    setIsSaveRequested,
    embeddingProgress,
    embeddingStatus,
    topicSelectionComponent,
  ]);
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
      color: currentTheme?.theme?.font_color,
      minWidth: isLargeScreen() ? 500 : 250,
      maxWidth: isLargeScreen() ? 500 : 250,
    }),
    input: (base) => ({
      ...base,
      color: currentTheme?.theme?.font_color, // This would change the color of the text inside the input
    }),
    option: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.font_color,
        cursor: "pointer",

        ":active": {
          ...styles[":active"],
          backgroundColor: currentTheme?.theme?.primary,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: currentTheme?.theme?.primary,
        },
      };
    },
    singleValue: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValue: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };
  const _statusAvailable = [
    { id: "suspended", name: t("mrcdocs.status.suspended") },
    { id: "published", name: t("mrcdocs.status.published") },
    { id: "archived", name: t("mrcdocs.status.archived") },
  ];
  if (documentStatus === "draft") {
    _statusAvailable.push({ id: "draft", name: t("mrcdocs.status.draft") });
  }
  const statusSelectionComponent = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: "column",
          alignItems: "flex-end",
          marginLeft: 20,
          zIndex: isTopicUpdateModeEnabled ? 1 : -1,
          minWidth: 100,
          marginTop: -10,
          position: "absolute",
          top: 52,
          right: 0,
        }}
      >
        <AppDropDown
          showTextInputLabel
          label={t("mrcdocs.form.status")}
          disabled={!isTopicUpdateModeEnabled}
          inputLabelStyle={{
            marginLeft: 0,
            marginRight: 10,
            marginBottom: 0,
          }}
          dropDownStyle={{
            minWidth: 100,
            borderColor: currentTheme?.theme?.border_color,
            maxWidth: !isLargeScreen() && 250,
          }}
          isElevated={false}
          dropdownIconColor="transparent"
          items={_statusAvailable}
          wrapperContainer={{
            flexDirection: "row",
            alignItems: "center",
          }}
          propertyLabel="name"
          propertyValue="id"
          selectedValue={documentStatus}
          onValueChange={(itemValue, itemIndex) => {
            onDocumentStatusChange(itemValue);
          }}
        />
        {<></>}
      </View>
    );
  });
  const topicSelectionComponent = useMemo(() => {
    return (
      <View
        style={{
          flex: 1,
          marginLeft: 17,
          height: 86,
          marginTop: -5,
          zIndex: isTopicUpdateModeEnabled ? 0 : -1,
        }}
      >
        <TopicsSelectionComponent
          showAddNew={isTopicUpdateModeEnabled}
          wrapperStyle={{
            paddingBottom: (!isPDF || isHTML || isRawText) && 0,
            paddingTop: (isPDF || isHTML || isRawText) && 0,
          }}
          isTopicUpdateModeEnabled={isTopicUpdateModeEnabled}
          showEditIcon={false}
          selectedTopics={selectedTopics}
          setIsTopicUpdateModeEnabled={setIsTopicUpdateModeEnabled}
          onSelectedTopicsChange={onSelectedTopicsChange}
          topics={topics}
          placeholder={t("mrcdocs.placeholders.selectTopic")}
          value={selectedTopics
            ?.filter((y) => topics?.find((t) => t?.id === y?.id))
            ?.map((u) => {
              return {
                ...u,
                backgroundColor:
                  topics?.find((t) => t?.id === u?.id)?.backgroundColor ||
                  currentTheme?.theme?.grey,
                emoji: topics?.find((t) => t?.id === u?.id)?.emoji || "📁",
                value: u?.id,
                label: u?.name || t("mrcdocs.ui.undefined"),
              };
            })}
        />
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {(getActiveWorkspace()?.subscriptionType == "business" ||
            getActiveWorkspace()?.subscriptionType == "enterprise") &&
            selectedNode?.data?.external_path && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  minWidth: 200,
                }}
              >
                <ToothFairySwitch
                  disabled={!isTopicUpdateModeEnabled}
                  wrapperStyle={{
                    flex: 1,
                    marginLeft: 10,
                    marginTop: 5,
                    zIndex: -1,
                    maxWidth: 200,
                    marginRight: 10,
                  }}
                  label={t("mrcdocs.form.advVectorisation")}
                  onChange={(v) => {
                    setAdvEmb(v);
                  }}
                  isTicked={advEmb}
                />
              </View>
            )}
          {(isPDF &&
            isPDFLoaded &&
            selectedNode?.data?.external_path?.split(".")?.pop() === "pdf") ||
          selectedNode?.data?.external_path?.split(".")?.pop() === "html" ? (
            <>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  minWidth: 200,
                }}
              >
                <ToothFairySwitch
                  disabled={
                    !isTopicUpdateModeEnabled ||
                    (!isExtractImagesAllowed &&
                      selectedNode?.data?.external_path?.split(".")?.pop() !=
                        "html")
                  }
                  wrapperStyle={{
                    flex: 1,

                    marginTop: 5,
                    zIndex: -1,
                    maxWidth: 200,
                  }}
                  label={t("mrcdocs.form.imgExtraction")}
                  onChange={(v) => {
                    setExtractImages(v);
                  }}
                  isTicked={extractImages}
                />
                {extractImages && isTopicUpdateModeEnabled && (
                  <Popable
                    numberOfLines={3}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 30,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={t("mrcdocs.ui.imagesExtractionGuidelines")}
                    action="hover"
                    position="bottom"
                  >
                    <AppIcon
                      icon={AppAssets.icons.cogWheel}
                      size={16}
                      wrapperStyle={{
                        marginLeft: 10,
                        marginTop: 5,
                      }}
                      isClickable
                      onPress={() => {
                        setIsIstructionsWindowOpened(true);
                      }}
                    />
                  </Popable>
                )}
              </View>

              {!isExtractImagesAllowed &&
                selectedNode?.data?.external_path?.split(".")?.pop() !=
                  "html" && (
                  <AppText
                    style={{
                      fontSize: 10,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                    wrapperStyle={{
                      marginLeft: 3,
                      position: "absolute",
                      top: 47,
                    }}
                    color={currentTheme?.theme?.darkGrey}
                  >
                    {t("mrcdocs.ui.imagesExtractionLimit")}
                  </AppText>
                )}
            </>
          ) : (
            <View
              style={{
                height: 35,
              }}
            ></View>
          )}
        </View>
      </View>
    );
  });
  const topicCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: 500,
          width: 450,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={isTopicModalOpen}
        setIsModalOpen={setIsTopicModalOpen}
        modalTitle={t("mrcdocs.modal.createTopic")}
      >
        <SettingsAI
          onEntityCreated={handleEntityCreation}
          settingType="topic"
        />
      </ToothFairyModal>
    );
  }, [isTopicModalOpen, setIsTopicModalOpen]);
  const imagesExtractionComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: 500,
          width: 450,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={isInstructionsWindowOpened}
        setIsModalOpen={setIsIstructionsWindowOpened}
        modalTitle={t("mrcdocs.modal.imageExtraction")}
      >
        <ImagesExtraction
          initialValues={{
            documentImgRetrievalInstructions,
          }}
          savingInProgress={savingInProgress}
          handleFormSubmit={(v) => {
            console.log("submit", v);
            handleImagesExtraction(v);
          }}
        />
      </ToothFairyModal>
    );
  }, [
    isInstructionsWindowOpened,
    setIsIstructionsWindowOpened,
    documentImgRetrievalInstructions,
    savingInProgress,
  ]);
  const _treeView = useMemo(() => {
    const headerComponents = [
      <div style={{ position: "relative" }} className="upload_pdf">
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 300,
            },
          ]}
          content={t("mrcdocs.ui.upload")}
          action="hover"
          position="right"
        >
          <AppIcon
            icon={AppAssets.icons.import}
            color={currentTheme?.theme?.darkGrey}
            isHoverable
            isClickable
            onPress={() => {
              setShowUploadDropdown(!showUploadDropdown);
            }}
            wrapperStyle={{
              marginRight: 5,
              marginLeft: 0,
              marginTop: 1,
            }}
          />
        </Popable>
        {showUploadDropdown && (
          <View
            style={{
              position: "absolute",
              top: 25,
              left: 0,
              backgroundColor: currentTheme?.theme?.softBackgroundColor,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: currentTheme?.theme?.border_color,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 4,
              elevation: 5,
              zIndex: 100,
              minWidth: 140,
            }}
          >
            <AppText
              preventTranslation
              isClickable
              onPress={() => {
                setShowUploadDropdown(false);
                !isPDFUploadInProgress && handleFileSelect();
              }}
              wrapperStyle={{
                padding: 10,
                borderBottomWidth: 1,
                borderBottomColor: currentTheme?.theme?.border_color,
              }}
              color={currentTheme?.theme?.font_color}
            >
              {t("mrcdocs.ui.uploadFiles")}
            </AppText>
            <AppText
              preventTranslation
              isClickable
              onPress={() => {
                setShowUploadDropdown(false);
                !isPDFUploadInProgress && handleArtifactSelect();
              }}
              wrapperStyle={{
                padding: 10,
                borderBottomWidth: 1,
                borderBottomColor: currentTheme?.theme?.border_color,
              }}
              color={currentTheme?.theme?.font_color}
            >
              {t("mrcdocs.ui.uploadArtifacts")}
            </AppText>
            <AppText
              preventTranslation
              isClickable
              onPress={() => {
                setShowUploadDropdown(false);
                !isPDFUploadInProgress && handleFolderSelect();
              }}
              wrapperStyle={{
                padding: 10,
              }}
              color={currentTheme?.theme?.font_color}
            >
              {t("mrcdocs.ui.uploadFolder")}
            </AppText>
          </View>
        )}
      </div>,
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("mrcdocs.ui.newCollabDoc")}
        action="hover"
        position="bottom"
      >
        <AppIcon
          icon={AppAssets.icons.file}
          color={currentTheme?.theme?.darkGrey}
          isHoverable
          isClickable
          onPress={() => {
            setDialogActionOnTree("NEW_DOCUMENT");
          }}
          wrapperStyle={{
            marginRight: 5,
            marginLeft: 3,
            marginTop: 1,
          }}
        />
      </Popable>,
      <AppText
        style={{
          fontWeight: "bold",
        }}
        color={currentTheme?.theme?.darkGrey}
        wrapperStyle={{
          marginHorizontal: 5,
        }}
      >
        |
      </AppText>,
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("mrcdocs.ui.newFolder")}
        action="hover"
        position="bottom"
      >
        <AppIcon
          icon={AppAssets.icons.folder}
          color={currentTheme?.theme?.darkGrey}
          isHoverable
          isClickable
          onPress={() => {
            setDialogActionOnTree("NEW_FOLDER");
          }}
          wrapperStyle={{
            marginRight: 5,
            marginLeft: 5,
            marginTop: 1,
          }}
        />
      </Popable>,
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("mrcdocs.ui.vectorSearch")}
        action="hover"
        position="bottom"
      >
        <AppIcon
          icon={AppAssets.icons.deepFileSearch}
          color={currentTheme?.theme?.darkGrey}
          isHoverable
          isClickable
          onPress={() => {
            setIsSearchModalOpen(true);
          }}
          wrapperStyle={{
            marginRight: 5,
            marginLeft: 5,
            marginTop: 1,
          }}
        />
      </Popable>,
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={
          isMultiDeleteMode
            ? t("mrcdocs.ui.exitMultiDelete")
            : t("mrcdocs.ui.multiDeleteMode")
        }
        action="hover"
        position="bottom"
      >
        <AppIcon
          icon={AppAssets.icons.plannerStep}
          color={
            isMultiDeleteMode
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.darkGrey
          }
          isHoverable
          isClickable
          onPress={() => {
            toggleMultiDeleteMode();
          }}
          wrapperStyle={{
            marginRight: 5,
            marginLeft: 5,
            marginTop: 1,
          }}
        />
      </Popable>,
      isMultiDeleteMode && multiSelectedIds.length > 0 && (
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 300,
            },
          ]}
          content={t("mrcdocs.ui.moveSelected", {
            defaultValue: "Move / edit selected",
          })}
          action="hover"
          position="bottom"
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginRight: 5,
              marginLeft: 5,
              marginTop: 1,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.export}
              color={currentTheme?.theme?.primary}
              isHoverable
              isClickable
              onPress={() => {
                setBulkMoveTargetFolderId("__no_folder_change__");
                setBulkMoveTopics(null);
                setBulkMovePublish(true);
                setShowBulkMoveModal(true);
              }}
              wrapperStyle={{}}
            />
          </View>
        </Popable>
      ),
      isMultiDeleteMode && multiSelectedIds.length > 0 && (
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 300,
            },
          ]}
          content={t("mrcdocs.ui.deleteSelected")}
          action="hover"
          position="bottom"
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginRight: 5,
              marginLeft: 5,
              marginTop: 1,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.delete}
              color={currentTheme?.theme?.red}
              isHoverable
              isClickable
              onPress={() => {
                setDialogActionOnTree("DELETE_MULTIPLE");
              }}
              wrapperStyle={{}}
            />
            <AppText
              preventTranslation
              style={{
                fontSize: 10,
                marginLeft: 2,
                color: currentTheme?.theme?.red,
                fontWeight: "bold",
              }}
            >
              {multiSelectedIds.length}
            </AppText>
          </View>
        </Popable>
      ),
    ].filter(Boolean);

    return (isTreeDataLoading || _coreTreeViewData === null) && !isCollapsed ? (
      <LoadingPlaceholder
        style={{
          padding: 10,
          maxWidth: 200,
        }}
        visible
        caption={t("mrcdocs.ui.loading")}
      />
    ) : (
      <div className="documents_tree">
        <AppTreeView
          headerComponents={headerComponents}
          showCollapseTree={false}
          title=" "
          heightOffset={125}
          filterPlaceholder={t("mrcdocs.placeholders.filterFoldersAndFiles")}
          foldersData={_coreTreeViewData}
          selectedNode={selectedNode}
          handleClickOnNode={handleClickOnNode}
          setSelectedNodeForTreeView={setSelectedNodeForTreeView}
          deleteNode={deleteNode}
          setDialogActionOnTree={setDialogActionOnTree}
          isEmpty={_coreTreeViewData?.length === 0}
          onDrop={onDrop}
          onDropFilesOnNode={onDropFilesOnNode}
          initialOpen={false}
          minWidth={!isLargeScreen() && minAppWidth() - 50}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          showIconsOnSelection={true}
          onMove={onMove}
          fixedNodeIds={[
            "mrcRoot",
            "qaRoot",
            "domainRoot",
            "artifactsRoot",
            "tfcodeSession",
          ]}
          isMultiDeleteMode={isMultiDeleteMode}
          multiSelectedIds={multiSelectedIds}
          onToggleNodeSelection={toggleNodeSelection}
        />
      </div>
    );
  }, [
    JSON.stringify(_coreTreeViewData),
    isTreeDataLoading,
    initialOpen,
    selectedNode?.id,
    isLargeScreen(),
    minAppWidth(),
    isCollapsed,
    getActiveWorkspace()?.id,
    folders?.length,
    setSelectedNodeForTreeView,
    currentTheme?.theme,
    isPDFUploadInProgress,
    handleFileSelect,
    handleFolderSelect,
    showUploadDropdown,
    setDialogActionOnTree,
    history,
    onDropFilesOnNode,
    isMultiDeleteMode,
    multiSelectedIds,
    toggleMultiDeleteMode,
    toggleNodeSelection,
  ]);
  const _actionsChoice = () => {
    return (
      <ToothFairyResponsiveOptionsModal
        isModalOpen={isModalOptionsOpen}
        setIsModalOpen={setIsModalOptionsOpen}
        modalTitle={t("mrcdocs.ui.actions")}
        height={300}
      >
        <ToothFairyClickableOption
          leftIcon={AppAssets.icons.copy}
          onPress={() => {
            setIsModalOptionsOpen(false);
            setLastModalOpen(Date.now());
            setDialogActionOnTree("NEW_FOLDER");
          }}
        >
          {t("mrcdocs.entity.folder")}
        </ToothFairyClickableOption>
        <ToothFairyClickableOption
          leftIcon={AppAssets.icons.copy}
          onPress={() => {
            setIsModalOptionsOpen(false);
            setLastModalOpen(Date.now());
            setDialogActionOnTree("NEW_DOCUMENT");
          }}
        >
          {t("mrcdocs.entity.liveDocument")}
        </ToothFairyClickableOption>
        <ToothFairyClickableOption
          leftIcon={AppAssets.icons.pdf}
          onPress={() => {
            setIsModalOptionsOpen(false);
            setLastModalOpen(Date.now());
            handleFileSelectPdf();
          }}
        >
          {t("mrcdocs.ui.uploadPdf")}
        </ToothFairyClickableOption>
      </ToothFairyResponsiveOptionsModal>
    );
  };

  const _coreHeaderComponent = useMemo(() => {
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <PageTitle>{t("mrcdocs.ui.knowledgeHub")}</PageTitle>
      </View>
    );
  }, []);
  return (
    <AppBaseScreen
      isScrollable={true}
      contentContainerStyle={{
        overflow: "auto",
        flex: 1,
      }}
    >
      <AppJoyRide
        handleCallback={(e) => {
          if (e?.status === "finished" || e?.status === "skipped") {
            completeTourIfEligible(TOUR_FLAGS.knowledgeHubTourCompleted);
            if (!user?.userHelperPreferences?.knowledgeHubVisited) {
              updateUserHelperPreferences(user, {
                knowledgeHubVisited: true,
              });
            }
          }
        }}
        run={
          isPlatformSupported &&
          knowledgeHubTourReady &&
          needsTour(TOUR_FLAGS.knowledgeHubTourCompleted)
        }
        steps={steps_first_interaction(t)}
        spotlightClicks={true}
      />
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: "relative", flex: 1, display: "flex" }}
      >
        {isFileDragActive && (
          <div
            style={{
              position: "absolute",
              bottom: 14,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              maxWidth: "90%",
              padding: "8px 16px",
              borderRadius: 20,
              backgroundColor: currentTheme?.theme?.primary,
              color: currentTheme?.theme?.white,
              display: "flex",
              alignItems: "center",
              gap: 8,
              pointerEvents: "none",
              boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
              fontSize: 13,
              fontWeight: "bold",
              fontFamily: "normalFont",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <AppIcon
              icon={AppAssets.icons.import}
              color={currentTheme?.theme?.white}
              size={16}
            />
            {t("mrcdocs.ui.dropHint")}
          </div>
        )}
        <View
          style={{
            paddingRight: 20,
            paddingLeft: 5,
            paddingVertical: 10,
            minWidth: minAppWidth(),
            flex: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              flex: 1,
              zIndex: -1,
            }}
          >
            {_treeView}
            {isLoading || isPDFUploadInProgress ? (
              <LoadingPlaceholder
                style={{
                  padding: 10,
                }}
                visible
                caption={t("mrcdocs.ui.loadingDocument")}
              />
            ) : isDocumentValid ? (
              <View
                style={{
                  flex: 1,
                  minWidth: minAppWidth() - 40,
                }}
              >
                <View
                  style={{
                    flexDirection: "column",
                    justifyContent: "space-between",
                    marginBottom: 10,

                    flexWrap: "wrap",
                    zIndex: 2,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      flex: 1,
                    }}
                  >
                    {topicSelectionComponent}
                    {(isPDF && isPDFLoaded) || (!isPDF && !isLoading) ? (
                      _savingNotification
                    ) : (
                      <></>
                    )}
                    {selectedNode?.data?.external_path && (
                      <View
                        style={{
                          marginTop: 10,
                          flexDirection: "row",
                          alignItems: "baseline",
                        }}
                      >
                        <AppIcon
                          icon={AppAssets?.icons?.separator}
                          color={currentTheme?.theme?.mediumGrey}
                          isActive={false}
                          size={18}
                          wrapperStyle={{
                            marginHorizontal: 3,
                          }}
                        />
                        <Popable
                          numberOfLines={2}
                          backgroundColor={
                            currentTheme?.theme?.tooltipBackgroundColor
                          }
                          style={[
                            {
                              top: 30,
                              zIndex: 20,
                              maxWidth: 300,
                            },
                          ]}
                          content={t("mrcdocs.ui.downloadDocument")}
                          action="hover"
                          position="left"
                        >
                          <AppIcon
                            icon={AppAssets.icons.export}
                            color={currentTheme?.theme?.primary}
                            isHoverable
                            isClickable
                            onPress={() => {
                              downloadDocument(
                                selectedNode?.data?.external_path,
                              );
                            }}
                            wrapperStyle={{
                              marginRight: 5,
                              marginLeft: 5,
                              marginTop: 1,
                            }}
                          />
                        </Popable>
                        <Popable
                          numberOfLines={2}
                          backgroundColor={
                            currentTheme?.theme?.tooltipBackgroundColor
                          }
                          style={[
                            {
                              top: 30,
                              zIndex: 20,
                              maxWidth: 300,
                            },
                          ]}
                          content={
                            isUpdateFileUploadInProgress
                              ? t("mrcdocs.ui.updateFileInProgress")
                              : t("mrcdocs.ui.updateFile")
                          }
                          action="hover"
                          position="left"
                        >
                          <AppIcon
                            icon={
                              isUpdateFileUploadInProgress
                                ? AppAssets.icons.progress
                                : AppAssets.icons.changeFile
                            }
                            color={currentTheme?.theme?.primary}
                            isHoverable
                            isClickable
                            onPress={() => {
                              onUpdateFileClick();
                            }}
                            wrapperStyle={{
                              marginRight: 5,
                              marginLeft: 5,
                              marginTop: 1,
                            }}
                          />
                        </Popable>
                      </View>
                    )}
                  </View>
                  {(isPDF && isPDFLoaded) || (!isPDF && !isLoading) ? (
                    <>{statusSelectionComponent}</>
                  ) : (
                    <View></View>
                  )}
                </View>
                {(() => {
                  if (selectedNode?.id == "3537") {
                    console.log("[TRACE 3537] Render decision:", {
                      type: selectedNode?.data?.type,
                      isArtifact: isArtifactType(selectedNode?.data?.type),
                      hasExternalPath: !!selectedNode?.data?.external_path,
                      parent: selectedNode?.data?.parent,
                    });
                  }
                  return null;
                })()}
                {selectedNode?.data?.type &&
                isArtifactType(selectedNode.data.type) ? (
                  <ArtifactViewer
                    path={selectedNode?.data?.external_path}
                    documentId={documentId}
                    onDownload={() =>
                      downloadDocument(selectedNode?.data?.external_path)
                    }
                  />
                ) : selectedNode?.data?.external_path ? (
                  selectedNode?.data?.external_path?.split(".")?.pop() ===
                  "pdf" ? (
                    <PDFViewer
                      path={selectedNode?.data?.external_path}
                      onPDFDocumentLoad={onPDFDocumentLoad}
                    />
                  ) : (
                    <>
                      <GenericViewer
                        path={selectedNode?.data?.external_path}
                        onDownloadClick={(path) => {
                          console.log("download", path);
                        }}
                        isPDFLoaded={isPDFLoaded}
                        onPDFDocumentLoad={onPDFDocumentLoad}
                        documentId={documentId}
                      />
                    </>
                  )
                ) : (
                  <EditorWrapper
                    {..._editorStyles}
                    isToolBarVisible
                    allowImages={true}
                    workspaceid={getActiveWorkspace()?.id}
                    editMode={true}
                    isPDF={
                      selectedNode?.data?.external_path != null &&
                      selectedNode?.data?.parent != "domainRoot"
                    }
                    allowExport
                    isPlaceHolderEnabled
                    placeholder={t("mrcdocs.placeholders.enterTextHere")}
                    onSocketConnectionChange={onSocketConnectionChange}
                    onError={handleEditorError}
                    value={value}
                    setValue={setValue}
                    documentId={documentId}
                    isInactive={isInactive}
                    words={wordsCount}
                    characters={charactersCount}
                    wordsLabel={t("editor.remainingWords")}
                    charactersLabel={t("editor.remainingCharacters")}
                    agcEnabled={agcEnabled}
                    fakeCheckInProgress={fakeCheckInProgress}
                    fakeCheckData={fakeCheckData}
                    onRecordClick={handleOnRecorderClick}
                    allowTranscription={true}
                    setEditor={setEditor}
                  />
                )}
              </View>
            ) : (
              <DelayedRender delay={3000}>
                <AppPlaceholderView
                  accessibilityLabel="doc-placeholder"
                  wrapperStyle={{
                    width: 325,
                    height: 220,
                    marginVertical: "auto",
                    marginHorizontal: "auto",
                  }}
                  size={30}
                  icon={AppAssets.icons.orbit}
                  mainCaption={
                    documentId
                      ? t("mrcdocs.ui.documentDoesNotExistCaption")
                      : t("mrcdocs.ui.selectDocumentCaption")
                  }
                  subCaption={
                    documentId
                      ? t("mrcdocs.ui.documentDoesNotExistSubCaption")
                      : t("mrcdocs.ui.selectDocumentSubCaption")
                  }
                />
                <div
                  style={{
                    width: 0,
                  }}
                  className="document_section"
                ></div>
              </DelayedRender>
            )}
          </View>
        </View>
      </div>
      {_coreModalView}
      {_bulkMoveModal}
      {topicCreationComponent}
      {_coreSourcesModelView}
      {imagesExtractionComponent}
      {_actionsChoice()}
      <DocumentSearchModal
        isModalOpen={isSearchModalOpen}
        setIsModalOpen={setIsSearchModalOpen}
        searchText={searchText}
        setSearchText={setSearchText}
        handleDocumentSelection={handleDocumentSelection}
        promiseOptions={promiseOptions}
        colourStyles={colourStyles}
      />
      {Platform.OS === "web" && (
        <input
          type="file"
          ref={updateFileInputRef}
          accept={`.${selectedNode?.data?.external_path
            ?.split(".")
            ?.pop()
            ?.toLowerCase()}`}
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e?.target?.files?.[0];
            if (file) updateDocumentFile(file);
          }}
        />
      )}
    </AppBaseScreen>
  );
};

export default MrcDocumentsView;
